/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { getConnectedEdges, Handle, useNodeId, useStore } from 'reactflow';

const selector = (s) => ({
  nodeInternals: s.nodeInternals,
  edges: s.edges
});

/**
 *
 * Custom handle component to limit the number of outgoing edges allowed from each handle for nodes with multiple edges
 *
 * @param {String} id (Optional) The id of the handle
 * @param {String} source (Optional) The source of the handle (id of the node)
 * @param {String} position The position of the handle (top, right, bottom, left)
 * @param {String} type The type of the handle (source or target)
 * @param {Element} icon (Optional) Custom icon element
 * @param {Number} allowedOutgoingEdgesCount (Optional) How many outgoing edges this handles is allowed to have
 * @param {Number} allowedIncomingEdgesCount (Optional) How many incoming edges this handles is allowed to have
 * @returns True or False, depending on if the handle can accept more edges
 */
const LimitedConnectionsHandle = (props) => {
  const { allowedIncomingEdgesCount, allowedOutgoingEdgesCount, position, icon } = props;
  const { nodeInternals, edges } = useStore(selector);
  const nodeId = useNodeId();

  const isHandleConnectable = useMemo(() => {
    const node = nodeInternals.get(nodeId);
    const connectedEdges = getConnectedEdges([node], edges);

    if (Number.isInteger(allowedOutgoingEdgesCount)) {
      const connectedEdgesFromHandle = connectedEdges.filter((edge) => {
        return edge.sourceHandle === props.id && (!props.source || edge.source === props.source);
      });

      return connectedEdgesFromHandle.length < allowedOutgoingEdgesCount;
    }

    if (allowedIncomingEdgesCount) {
      return connectedEdges.length < allowedIncomingEdgesCount;
    }

    return true;
  }, [nodeInternals, edges, nodeId, allowedOutgoingEdgesCount]);

  return (
    <div className={icon ? 'm-2' : ''}>
      <Handle {...props} isConnectable={isHandleConnectable}>
        {
          icon ? (
            <div style={{
              pointerEvents: 'none', // Ignore pointer events for icon to not interfere with connect edge
              position: 'relative', // Make the container a positioning context
              ...(position === 'bottom' && { left: '-5px', top: '-20px' }),
              ...(position === 'left' && { right: '-8px', top: '-10px' }),
              ...(position === 'right' && { left: '-16px', top: '-10px' }),
              ...(position === 'top' && { left: '-5px', top: '5px' })
            }}>
              {icon}
            </div>
          ) : null
        }
      </Handle>
    </div>
  );
};

// https://reactflow.dev/api-reference/types/handle/
LimitedConnectionsHandle.propTypes = {
  id: PropTypes.string,
  allowedOutgoingEdgesCount: PropTypes.number,
  allowedIncomingEdgesCount: PropTypes.number,
  source: PropTypes.string,
  position: PropTypes.string,
  icon: PropTypes.element
};

export default LimitedConnectionsHandle;
